import React, { useState } from 'react';
import clsx from 'clsx';
import { CircleX } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import Link from '@dashboard/components/Link';
import TicketCode from '@dashboard/components/TicketCode';
import Tooltip from '@dashboard/components/Tooltip';
import { Checkbox } from '@dashboard/components/ui/checkbox';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import {
  chargeEmailLogsKey,
  chargeGuestsKey,
  chargeKey,
  chargeTicketsKey,
} from '@dashboard/lib/queryKeys';
import isAbandonedAbandonment from '@dashboard/services/abandonment/isAbandonedAbandonment';
import {
  formatPrice,
  formatPriceForInput,
  formatPriceInput,
} from '@dashboard/services/formatting/price';
import { calculateMaximumRefundAmount } from '@dashboard/services/payments';
import { fetcher } from '@dashboard/services/request';

const COPY = {
  booking: {
    action: 'Cancel booking',
    description:
      'Canceling a booking voids its tickets and they go back on sale. Optionally issue a partial or full refund.',
    refund: 'Refund booking',
    canceled: 'This booking has been canceled',
    incomplete: "This booking isn't complete yet",
    failed: "This booking didn't go through",
    success: 'Booking canceled',
  },
  payment: {
    action: 'Cancel payment',
    description:
      "Canceling a payment cancels the whole order, including order bumps, puts their stock back and removes the customer's access to its files and licence key. It does not refund the customer unless you choose to.",
    refund: 'Refund payment',
    canceled: 'This payment has been canceled',
    incomplete: "This payment isn't complete yet",
    failed: "This payment didn't go through",
    success: 'Payment canceled',
  },
};

const TICKETS_DESCRIPTION =
  'Canceled tickets are voided and go back on sale. Optionally issue a partial or full refund.';

const DID_NOT_GO_THROUGH = ['failed', 'canceled', 'draft_expired'];

const REFUND_REASONS = [
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: 'requested_by_customer', label: 'Requested by customer' },
];

export const cancelOffReason = (payment, noun) => {
  const copy = COPY[noun];
  return (
    (payment.orderStatus === 'canceled' && copy.canceled) ||
    (isAbandonedAbandonment(payment) && 'This checkout was abandoned') ||
    ((payment.status === 'created' || payment.status === 'draft') && copy.incomplete) ||
    (DID_NOT_GO_THROUGH.includes(payment.status) && copy.failed) ||
    ''
  );
};

const refundOffReason = (payment, maximumRefundAmount) =>
  (payment.isComplimentary && 'A complimentary booking has no payment to refund') ||
  (payment.status === 'refunded' && 'This booking is already refunded') ||
  (payment.stripePaymentIntentId == null && 'Only payments taken through Stripe can be refunded') ||
  (maximumRefundAmount <= 0 && 'Nothing is left to refund') ||
  '';

const ticketsTitle = count =>
  count === 1 ? 'Cancel ticket' : count > 1 ? `Cancel ${count} tickets` : 'Cancel tickets';

const TicketCard = ({ ticket, checked, onCheckedChange, disabled }) => {
  const id = `cancel-ticket-${ticket._id}`;
  const name = ticket.attendee?.name || ticket.customerName;
  const email = ticket.attendee?.email || ticket.customerEmail;
  const canceled = ticket.status === 'CANCELED';
  return (
    <label
      htmlFor={id}
      className={clsx(
        'flex items-start gap-3 rounded-md border border-input px-3 py-2 text-sm',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <Checkbox
        id={id}
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onCheckedChange={value => onCheckedChange(value === true)}
      />
      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        <span className="block font-medium text-gray-900 dark:text-foreground">
          {name || email}
          {canceled ? ' (canceled)' : ''}
        </span>
        {name && email ? <span className="block text-gray-500">{email}</span> : null}
        <span className="mt-2 flex flex-wrap items-center gap-2 text-gray-900 dark:text-foreground">
          <span className="min-w-0">{ticket.ticketType?.name}</span>
          <TicketCode code={ticket.ticketShortId} copy={false} />
        </span>
      </span>
    </label>
  );
};

/**
 * The cancel form. On a booking, `ticketId` cancels just that ticket (opened from an attendee);
 * otherwise the seller cancels all tickets or picks some. `prefill` carries a refund over from
 * the Refund booking dialog.
 */
export const CancelChargeFormDialog = ({
  payment,
  noun,
  open,
  onOpenChange,
  ticketId,
  prefill,
}) => {
  const copy = COPY[noun];
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const maximumRefundAmount = calculateMaximumRefundAmount(payment);
  const isBooking = noun === 'booking';
  const [scope, setScope] = useState('all');
  const [picked, setPicked] = useState(ticketId ? [ticketId] : []);

  const { data: ticketData } = useQuery({
    queryKey: chargeTicketsKey(payment._id),
    queryFn: () => fetcher(`/sellers/${payment.seller}/tickets/${payment._id}`, token),
    enabled: open && isBooking && !ticketId,
  });
  const tickets = ticketData?.tickets ?? [];
  const byTicket = Boolean(ticketId) || scope === 'selected';

  const title = !isBooking || !byTicket ? copy.action : ticketsTitle(picked.length);
  const description = isBooking && byTicket ? TICKETS_DESCRIPTION : copy.description;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });
    queryClient.invalidateQueries({ queryKey: chargeTicketsKey(payment._id) });
    queryClient.invalidateQueries({ queryKey: chargeGuestsKey(payment._id) });
    queryClient.invalidateQueries({ queryKey: chargeEmailLogsKey(payment._id) });
    queryClient.invalidateQueries({ queryKey: ['pageCustomers'] });
    queryClient.invalidateQueries({ queryKey: ['attendees'] });
  };

  const cancel = useMutation({
    mutationFn: ({ path, body }) =>
      fetcher(`/sellers/${payment.seller}/charges/${payment._id}/${path}`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // A failure can come after the cancel committed, so refresh either way.
    onSettled: refresh,
    onSuccess: (resp, { path }) => {
      const whole = path === 'cancel' || resp?.bookingCanceled;
      toast({
        title: whole ? copy.success : picked.length === 1 ? 'Ticket canceled' : 'Tickets canceled',
        ...(path === 'cancel' &&
          isBooking && {
            description: `Additionally ${resp?.canceled ?? 0} tickets were also canceled.`,
          }),
        type: 'success',
      });
      onOpenChange(false);
    },
    onError: error => {
      toast({
        title: `An error occurred while canceling the ${byTicket ? 'tickets' : noun}`,
        description: error?.message,
        type: 'error',
      });
    },
  });

  const refundOff = refundOffReason(payment, maximumRefundAmount);
  const fullRefund = formatPriceForInput({
    amount: maximumRefundAmount,
    currency: payment.currency,
    style: 'decimal',
  });

  const togglePicked = (id, on) =>
    setPicked(current => (on ? [...current, id] : current.filter(other => other !== id)));

  const ticketFields = !isBooking
    ? []
    : ticketId
      ? []
      : [
          { type: 'heading', label: 'Tickets', level: 3, className: 'mb-3' },
          {
            type: 'custom',
            name: 'ticketScope',
            renderField: (_field, { setFieldValue }) => (
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Tickets">
                  {[
                    { value: 'all', label: 'All tickets', hint: 'Cancels the booking' },
                    {
                      value: 'selected',
                      label: 'Selected tickets',
                      hint: 'Pick which tickets to cancel',
                      disabled: tickets.length === 0,
                    },
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={scope === option.value}
                      disabled={option.disabled}
                      onClick={() => {
                        setScope(option.value);
                        setFieldValue(
                          'refundAmount',
                          option.value === 'all' ? fullRefund : (prefill?.refundAmount ?? ''),
                        );
                      }}
                      className={clsx(
                        'relative rounded-md border px-3 py-2 pr-9 text-left text-sm',
                        scope === option.value ? 'border-primary' : 'border-input',
                        option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                      )}
                    >
                      <span className="block font-medium">{option.label}</span>
                      <span className="block text-xs text-muted-foreground">{option.hint}</span>
                      <span
                        className={clsx(
                          'absolute right-2 top-2 flex size-4 items-center justify-center rounded-full border',
                          scope === option.value ? 'border-primary' : 'border-input',
                        )}
                      >
                        {scope === option.value ? (
                          <span className="size-2 rounded-full bg-primary" />
                        ) : null}
                      </span>
                    </button>
                  ))}
                </div>
                {scope === 'selected' ? (
                  <div className="mt-3 flex flex-col gap-2">
                    {tickets.map(ticket => (
                      <TicketCard
                        key={ticket._id}
                        ticket={ticket}
                        checked={picked.includes(ticket._id)}
                        disabled={ticket.status === 'CANCELED'}
                        onCheckedChange={on => togglePicked(ticket._id, on)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ),
          },
          { type: 'divider' },
        ];

  return (
    <Dialog
      title={title}
      description={description}
      cancelButton={null}
      confirmButton={null}
      size="md"
      open={open}
      onCancel={() => onOpenChange(false)}
    >
      {() => (
        <Form
          showState={false}
          initialValues={{
            reason: '',
            sendCancellationEmail: true,
            includeRefund: Boolean(prefill),
            refundReason: prefill?.refundReason ?? '',
            refundReasonNote: prefill?.refundReasonNote ?? '',
            refundAmount: prefill?.refundAmount ?? (ticketId ? '' : fullRefund),
            sendRefundEmail: true,
          }}
          onSubmit={async values => {
            const refund = values.includeRefund
              ? {
                  amount: formatPriceInput(values.refundAmount, payment.currency),
                  reason: values.refundReason,
                  note: values.refundReasonNote || undefined,
                }
              : undefined;
            const request = byTicket
              ? {
                  path: 'tickets/cancel',
                  body: {
                    ticketIds: picked,
                    reason: values.reason,
                    sendCancellationEmail: values.sendCancellationEmail,
                    ...(refund && { refund }),
                  },
                }
              : {
                  path: 'cancel',
                  body: {
                    reason: values.reason,
                    sendCancellationEmail: values.sendCancellationEmail,
                    includeRefund: Boolean(refund),
                    ...(refund && {
                      refundAmount: refund.amount,
                      refundReason: refund.reason,
                      refundReasonNote: refund.note,
                      sendRefundEmail: !isBooking && values.sendRefundEmail,
                    }),
                  },
                };
            try {
              return { data: await cancel.mutateAsync(request) };
            } catch (error) {
              return { error };
            }
          }}
          fields={({ values }) => [
            ...ticketFields,
            ...(isBooking ? [{ type: 'heading', label: 'Cancellation', level: 3 }] : []),
            {
              name: 'reason',
              label: 'Reason for cancellation',
              tags: ['not shown to customer'],
              type: 'textarea',
            },
            {
              name: 'sendCancellationEmail',
              label: 'Send cancellation email to customer',
              type: 'checkbox',
            },
            ...(isBooking
              ? [{ type: 'divider' }, { type: 'heading', label: 'Refund', level: 3 }]
              : []),
            {
              name: 'includeRefund',
              label: copy.refund,
              type: 'checkbox',
              disabled: Boolean(refundOff),
              tooltip: refundOff || undefined,
            },
            {
              name: 'refundAmount',
              label: `Refund amount`,
              tags: [
                `${formatPrice({ amount: maximumRefundAmount, currency: payment.currency })} left to refund`,
              ],
              type: 'number',
              visible: values.includeRefund,
              required: values.includeRefund,
            },
            {
              name: 'refundReason',
              label: 'Refund reason',
              type: 'select',
              visible: values.includeRefund,
              required: values.includeRefund,
              placeholder: 'Select a reason',
              options: REFUND_REASONS,
            },
            {
              name: 'refundReasonNote',
              label: 'Refund note',
              tags: ['not shown to customer'],
              type: 'textarea',
              visible: values.includeRefund,
            },
            // A booking has the one email; a ticked cancellation email already carries the refund.
            ...(isBooking
              ? []
              : [
                  {
                    name: 'sendRefundEmail',
                    label: 'Send refund email to customer',
                    type: 'checkbox',
                    visible: values.includeRefund && !values.sendCancellationEmail,
                  },
                ]),
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: title,
            appearance: 'primary',
            disabled: byTicket && picked.length === 0,
          }}
        />
      )}
    </Dialog>
  );
};

const CancelChargeDialog = ({ payment, noun, prefill, open: controlledOpen, onOpenChange }) => {
  const copy = COPY[noun];
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = onOpenChange ?? setOwnOpen;
  const tooltipLabel = cancelOffReason(payment, noun);

  return (
    <>
      <Tooltip triggerClassName="w-fit" label={tooltipLabel} side="right">
        <Link
          onClick={() => setOpen(true)}
          className={clsx('w-full flex gap-x-2 items-center no-underline', {
            'pointer-events-none opacity-50 cursor-not-allowed': tooltipLabel,
          })}
        >
          <>
            <CircleX className="w-4 h-4" />
            {copy.action}
          </>
        </Link>
      </Tooltip>

      {open ? (
        <CancelChargeFormDialog
          payment={payment}
          noun={noun}
          open={open}
          onOpenChange={setOpen}
          prefill={prefill}
        />
      ) : null}
    </>
  );
};

export default CancelChargeDialog;
